import { memo, type ReactNode, type Ref } from 'react';
import { Tooltip } from '@mantine/core';
import { IconFilter, IconChevronDown } from '@tabler/icons-react';
import styles from './FilterIconButton.module.css';

/** Size variant → class name. The standard 30px size is the no-prop default. */
const SIZE_CLASS_NAMES = {
  compact: styles.compactButton,
  input: `${styles.filterButton} ${styles.inputSize}`,
} as const;

export interface FilterIconButtonProps {
  ref?: Ref<HTMLButtonElement>;
  hasActiveFilters: boolean;
  opened?: boolean;
  onClick: () => void;
  size?: 'compact' | 'input';
  tooltip?: string;
  className?: string;
  'aria-label'?: string;
  children?: ReactNode;
  showChevron?: boolean;
}

/**
 * Reusable filter icon button component with active indicator dot
 *
 * Features:
 * - Consistent visual style across the app
 * - Active filters indicator (dot in top-right corner)
 * - Light/dark mode support
 * - Three size variants: standard (30px), input (36px, matches an adjacent search input) and compact (28px)
 *
 * @example Standard variant (TasksPanel)
 * <FilterIconButton
 *   hasActiveFilters={hasFilters}
 *   opened={isOpen}
 *   onClick={handleToggle}
 *   tooltip="Filters"
 * />
 *
 * @example Compact variant (AddressSearchBar)
 * <FilterIconButton
 *   hasActiveFilters={hasFilters}
 *   onClick={handleToggle}
 *   size="compact"
 *   tooltip="Filter results"
 * />
 */
export const FilterIconButton = memo(function FilterIconButton({
  ref,
  hasActiveFilters,
  opened = false,
  onClick,
  size,
  tooltip,
  className = '',
  'aria-label': ariaLabel,
  children,
  showChevron = false,
}: FilterIconButtonProps) {
  const iconSize = 16;
  const buttonStyle = size ? SIZE_CLASS_NAMES[size] : styles.filterButton;

  const button = (
    <button
      ref={ref}
      type="button"
      className={`${buttonStyle} ${opened ? styles.opened : ''} ${className}`}
      onClick={onClick}
      aria-label={ariaLabel}
      data-active={hasActiveFilters}
    >
      <span className={styles.iconWrapper}>
        <IconFilter className={styles.filterIcon} size={iconSize} />
        {hasActiveFilters && <span className={styles.filterDot} />}
      </span>
      {children}
      {showChevron && (
        <IconChevronDown
          className={styles.chevronIcon}
          data-opened={opened}
          size={16}
        />
      )}
    </button>
  );

  if (tooltip) {
    return (
      <Tooltip label={tooltip} position="bottom" withArrow>
        {button}
      </Tooltip>
    );
  }

  return button;
});
